import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { JobCardStatus as JobCardStatusType } from '@/types/jobCards';
import { useToast } from '@/hooks/useToast';
import { 
  validateStatusTransition, 
  UserRole, 
  formatValidationErrors,
  hasPermission 
} from '@/utils/jobCardValidation';
import {
  createErrorResponse,
  createSuccessResponse,
  createJobCardError,
  ERROR_CODES,
  withErrorHandling
} from '@/utils/jobCardErrorHandling';

export interface JobCardTimeLog {
  id: string;
  job_card_id: string;
  user_id: string;
  activity_type: 'setup_start' | 'run_start' | 'pause' | 'resume' | 'finish';
  description: string;
  metadata: {
    timestamp: string;
    elapsed_setup?: number;
    elapsed_run?: number;
    status_before?: string;
    status_after?: string;
    quantities?: {
      good?: number;
      scrap?: number;
      rework?: number;
    };
  };
  created_at: string;
}

export interface JobCardStatus {
  id: string;
  status: 'Not Started' | 'In Progress' | 'Paused' | 'QC Hold' | 'Completed' | 'Cancelled';
  actual_start_time?: string;
  actual_end_time?: string;
  quantity_completed: number;
  scrap_quantity: number;
  current_timer?: number;
}

export const useJobCardOperations = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Start Job Card - Creates time log and updates status
  const startJob = useCallback(async (jobCardId: string, operatorId: string, userRole: UserRole = 'operator') => {
    setLoading(true);
    setError(null);

    try {
      // 1. Validate Job status and fetch job card details
      const { data: jobCard, error: fetchError } = await supabase
        .from('job_cards')
        .select('*, work_order_operations!inner(*)')
        .eq('id', jobCardId)
        .single();

      if (fetchError) {
        const errorResponse = createErrorResponse(fetchError, {
          showToast: true,
          severity: 'error',
        });
        setError(errorResponse.error);
        toast({
          title: 'Database Error',
          description: errorResponse.error,
          variant: 'destructive',
        });
        return errorResponse;
      }

      if (!jobCard) {
        const error = createJobCardError(ERROR_CODES.JOB_NOT_FOUND);
        const errorResponse = createErrorResponse(error);
        setError(errorResponse.error);
        toast({
          title: 'Error',
          description: errorResponse.error,
          variant: 'destructive',
        });
        return errorResponse;
      }

      // 2. Validate status transition
      const validationResult = validateStatusTransition(
        jobCard.status,
        'In Progress',
        userRole
      );

      if (!validationResult.isValid) {
        const errorMessage = formatValidationErrors(validationResult.errors);
        setError(errorMessage);
        toast({
          title: 'Validation Error',
          description: errorMessage,
          variant: 'destructive',
        });
        return createErrorResponse(validationResult.errors);
      }

      const now = new Date().toISOString();
      const isFirstStart = jobCard.status === 'Not Started';
      const activityType = isFirstStart ? 'start' : 'resume';
      const newStatus = isFirstStart ? 'In Progress' : 'In Progress';

      // Debug session and user context
      const { data: { session } } = await supabase.auth.getSession();
      console.log('useJobCardOperations - Current session:', session);
      console.log('useJobCardOperations - Operator ID being used:', operatorId);
      console.log('useJobCardOperations - Session user ID:', session?.user?.id);
      
      // 2. Insert TimeLog record (activity)
      const { error: activityError } = await supabase
        .from('job_card_activities')
        .insert({
          job_card_id: jobCardId,
          user_id: operatorId,
          activity_type: activityType,
          description: isFirstStart 
            ? 'Job started - Setup phase initiated' 
            : 'Job resumed from pause',
          metadata: {
            timestamp: now,
            status_before: jobCard.status,
            status_after: newStatus,
          }
        });

      if (activityError) throw activityError;

      // 3. Set job_card.actual_start_time = now, status = In Progress
      const updateData: any = {
        status: newStatus,
        updated_at: now,
      };

      if (isFirstStart) {
        updateData.actual_start_time = now;
      }

      const { error: updateError } = await supabase
        .from('job_cards')
        .update(updateData)
        .eq('id', jobCardId);

      if (updateError) throw updateError;

      // 4. Update Workstation status = Busy (if workstation_id exists)
      const { data: jobWithWorkstation } = await supabase
        .from('job_cards')
        .select('workstation_id')
        .eq('id', jobCardId)
        .single();

      if (jobWithWorkstation?.workstation_id) {
        await supabase
          .from('workstations')
          .update({ 
            status: 'Busy',
            current_job_card_id: jobCardId,
            updated_at: now 
          })
          .eq('id', jobWithWorkstation.workstation_id);
      }

      // 5. Publish event job.started (we'll implement WebSocket later)
      console.log('Event: job.started', { 
        job_id: jobCardId, 
        operator_id: operatorId, 
        timestamp: now,
        workstation_id: jobWithWorkstation?.workstation_id 
      });

      toast({
        title: 'Job Started',
        description: isFirstStart 
          ? 'Job card started successfully - Setup phase initiated' 
          : 'Job card resumed successfully',
      });

      return { success: true, status: newStatus };

    } catch (error: any) {
      console.error('Error starting job:', error);
      setError(error.message);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Pause Job Card
  const pauseJob = useCallback(async (jobCardId: string, operatorId: string, elapsedTime?: number) => {
    setLoading(true);
    setError(null);

    try {
      // 1. Validate Job status = In Progress
      const { data: jobCard, error: fetchError } = await supabase
        .from('job_cards')
        .select('id, status, actual_start_time')
        .eq('id', jobCardId)
        .single();

      if (fetchError) throw fetchError;

      if (!['In Progress'].includes(jobCard.status)) {
        throw new Error(`Cannot pause job with status: ${jobCard.status}`);
      }

      const now = new Date().toISOString();

      // 2. Close currently open TimeLog row (end_time=now)
      const { error: activityError } = await supabase
        .from('job_card_activities')
        .insert({
          job_card_id: jobCardId,
          user_id: operatorId,
          activity_type: 'pause',
          description: 'Job paused by operator',
          metadata: {
            timestamp: now,
            status_before: jobCard.status,
            status_after: 'Paused',
            elapsed_time: elapsedTime || 0,
          }
        });

      if (activityError) throw activityError;

      // 3. Update job_card.status = Paused
      const { error: updateError } = await supabase
        .from('job_cards')
        .update({
          status: 'Paused',
          updated_at: now,
        })
        .eq('id', jobCardId);

      if (updateError) throw updateError;

      // 4. Update Workstation status = Available
      const { data: jobWithWorkstation } = await supabase
        .from('job_cards')
        .select('workstation_id')
        .eq('id', jobCardId)
        .single();

      if (jobWithWorkstation?.workstation_id) {
        await supabase
          .from('workstations')
          .update({ 
            status: 'Available',
            current_job_card_id: null,
            updated_at: now 
          })
          .eq('id', jobWithWorkstation.workstation_id);
      }

      // 5. Event job.paused
      console.log('Event: job.paused', { 
        job_id: jobCardId, 
        operator_id: operatorId, 
        timestamp: now 
      });

      toast({
        title: 'Job Paused',
        description: 'Job card paused successfully',
      });

      return { success: true, status: 'Paused' };

    } catch (error: any) {
      console.error('Error pausing job:', error);
      setError(error.message);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Complete Job Card
  const completeJob = useCallback(async (
    jobCardId: string, 
    operatorId: string, 
    quantities: { good: number; scrap: number; rework: number }
  ) => {
    setLoading(true);
    setError(null);

    try {
      // 1. Validate: GoodQty > 0 or ScrapQty > 0, and QC pass (when required)
      const { data: jobCard, error: fetchError } = await supabase
        .from('job_cards')
        .select(`
          id, 
          status, 
          quantity,
          work_order_operation_id,
          work_order_operations!inner(qc_required)
        `)
        .eq('id', jobCardId)
        .single();

      if (fetchError) throw fetchError;

      if (!['In Progress'].includes(jobCard.status)) {
        throw new Error(`Cannot complete job with status: ${jobCard.status}`);
      }

      if (quantities.good <= 0 && quantities.scrap <= 0) {
        throw new Error('Cannot complete job without good or scrap quantities');
      }

      // Check QC requirements if needed
      const qcRequired = jobCard.work_order_operations.qc_required;
      if (qcRequired) {
        const { data: qcChecks } = await supabase
          .from('work_order_qc')
          .select('result')
          .eq('operation_id', jobCard.work_order_operation_id);

        const hasFailedQC = qcChecks?.some(qc => qc.result === 'Fail');
        const hasAllPassedQC = qcChecks?.every(qc => qc.result === 'Pass');

        if (hasFailedQC) {
          throw new Error('Cannot complete job with failed QC checks');
        }

        if (!hasAllPassedQC) {
          throw new Error('QC checks must be completed before finishing job');
        }
      }

      const now = new Date().toISOString();

      // 2. Close final TimeLog
      const { error: activityError } = await supabase
        .from('job_card_activities')
        .insert({
          job_card_id: jobCardId,
          user_id: operatorId,
          activity_type: 'complete',
          description: 'Job completed by operator',
          metadata: {
            timestamp: now,
            status_before: jobCard.status,
            status_after: 'Completed',
            quantities: quantities,
          }
        });

      if (activityError) throw activityError;

      // 3. job_card.status = Done, actual_end = now
      const { error: updateError } = await supabase
        .from('job_cards')
        .update({
          status: 'Completed',
          actual_end_time: now,
          quantity_completed: quantities.good,
          scrap_quantity: quantities.scrap,
          rework_quantity: quantities.rework || 0,
          updated_at: now,
        })
        .eq('id', jobCardId);

      if (updateError) throw updateError;

      // 4. Update work order operation status if this completes the operation
      await supabase
        .from('work_order_operations')
        .update({
          status: 'Completed',
          actual_end_time: now,
          updated_at: now,
        })
        .eq('id', jobCard.work_order_operation_id);

      // 5. Update Workstation status = Available
      const { data: jobWithWorkstation } = await supabase
        .from('job_cards')
        .select('workstation_id')
        .eq('id', jobCardId)
        .single();

      if (jobWithWorkstation?.workstation_id) {
        await supabase
          .from('workstations')
          .update({ 
            status: 'Available',
            current_job_card_id: null,
            updated_at: now 
          })
          .eq('id', jobWithWorkstation.workstation_id);
      }

      // 6. Event job.finished
      console.log('Event: job.finished', { 
        job_id: jobCardId, 
        operator_id: operatorId, 
        timestamp: now,
        quantities 
      });

      toast({
        title: 'Job Completed',
        description: `Job completed successfully. Good: ${quantities.good}, Scrap: ${quantities.scrap}`,
      });

      return { success: true, status: 'Completed' };

    } catch (error: any) {
      console.error('Error completing job:', error);
      setError(error.message);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Get job card with activities
  const getJobCardWithActivities = useCallback(async (jobCardId: string) => {
    try {
      const { data, error } = await supabase
        .from('job_cards')
        .select(`
          *,
          work_order_operations!inner(
            id,
            operation_id,
            sequence_no,
            workstation_id,
            process_time,
            setup_time,
            qc_required,
            status,
            operations!inner(name, description)
          ),
          workstations(id, name, status),
          job_card_activities(
            id,
            activity_type,
            description,
            metadata,
            created_at,
            user_id
          )
        `)
        .eq('id', jobCardId)
        .single();

      if (error) throw error;
      
      // If we have job card activities, fetch user data for them
      if (data && data.job_card_activities && data.job_card_activities.length > 0) {
        // Extract unique user IDs from the activities
        const userIds = [...new Set(data.job_card_activities.map((activity: any) => activity.user_id).filter(Boolean))];
        
        // Fetch user data from public.users table
        let userData: any[] = [];
        if (userIds.length > 0) {
          const { data: users, error: userError } = await supabase
            .from('users')
            .select('id, email, full_name')
            .in('id', userIds);
          
          if (userError) {
            console.error('Error fetching user data:', userError);
          } else {
            userData = users || [];
          }
        }
        
        // Create a map of user data for quick lookup
        const userMap = new Map(userData.map(user => [user.id, user]));
        
        // Add user information to each activity
        const activitiesWithUsers = data.job_card_activities.map((activity: any) => {
          const user = userMap.get(activity.user_id);
          
          // Extract user name from full_name if available
          let userName = 'Unknown User';
          if (user?.full_name) {
            userName = user.full_name;
          } else if (user?.email) {
            userName = user.email;
          }
          
          return {
            ...activity,
            user_name: userName
          };
        });
        
        // Return the job card with activities that have user information
        return { 
          data: { 
            ...data, 
            job_card_activities: activitiesWithUsers 
          }, 
          error: null 
        };
      }
      
      return { data, error: null };
    } catch (error: any) {
      console.error('Error fetching job card:', error);
      return { data: null, error: error.message };
    }
  }, []);

  return {
    startJob,
    pauseJob,
    completeJob,
    getJobCardWithActivities,
    loading,
    error,
  };
};
